'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowDownCircle, ArrowUpCircle, CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { trpc } from '@/utils/trpc';

const recurringFormSchema = z.object({
  amount: z
    .string()
    .min(1, 'Valor é obrigatório')
    .refine(
      (val) =>
        !isNaN(Number(val.replace(',', '.'))) &&
        Number(val.replace(',', '.')) > 0,
      'Digite um valor válido maior que zero'
    ),
  type: z.enum(['debit', 'credit'], {
    required_error: 'Selecione o tipo da transação',
  }),
  categoryId: z.string().optional(),
  description: z.string().min(1, 'Descrição é obrigatória'),
  frequency: z.enum(
    ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'],
    {
      required_error: 'Selecione a frequência',
    }
  ),
  dayOfMonth: z.number().min(1).max(31).optional(),
  dayOfWeek: z.number().min(0).max(6).optional(),
  startDate: z.date({
    required_error: 'Data de início é obrigatória',
  }),
  endDate: z.date().optional(),
  isActive: z.boolean().default(true),
  isFixedExpense: z.boolean().default(false),
});

type RecurringFormData = z.infer<typeof recurringFormSchema>;

interface RecurringFormProps {
  open: boolean;
  onClose: () => void;
  editingId?: string | null;
  onSuccess: () => void;
}

export function RecurringForm({
  open,
  onClose,
  editingId,
  onSuccess,
}: RecurringFormProps) {
  const [selectedFrequency, setSelectedFrequency] = useState<string>('');

  const form = useForm<RecurringFormData>({
    resolver: zodResolver(recurringFormSchema),
    defaultValues: {
      amount: '',
      description: '',
      startDate: new Date(),
      isActive: true,
      isFixedExpense: false,
    },
  });

  // Fetch categories for dropdown
  const categories = useQuery(trpc.categories.list.queryOptions({}));

  // Fetch existing recurring transaction if editing
  const existingRecurring = useQuery({
    ...trpc.recurring.get.queryOptions({ id: editingId! }),
    enabled: !!editingId,
  });

  const createMutation = useMutation(
    trpc.recurring.create.mutationOptions({
      onSuccess: () => {
        onSuccess();
        form.reset();
      },
    })
  );

  const updateMutation = useMutation(
    trpc.recurring.update.mutationOptions({
      onSuccess: () => {
        onSuccess();
        form.reset();
      },
    })
  );

  // Populate form when editing
  useEffect(() => {
    if (existingRecurring.data && editingId) {
      const data = existingRecurring.data;
      form.reset({
        amount: data.amount,
        type: data.type,
        categoryId: data.categoryId || undefined,
        description: data.description,
        frequency: data.frequency,
        dayOfMonth: data.dayOfMonth || undefined,
        dayOfWeek: data.dayOfWeek || undefined,
        startDate: new Date(data.startDate),
        endDate: data.endDate ? new Date(data.endDate) : undefined,
        isActive: data.isActive,
        isFixedExpense: data.isFixedExpense || false,
      });
      setSelectedFrequency(data.frequency);
    }
  }, [existingRecurring.data, editingId, form]);

  const onSubmit = (data: RecurringFormData) => {
    const payload = {
      ...data,
      amount: data.amount.replace(',', '.'),
      startDate: data.startDate.toISOString(),
      endDate: data.endDate?.toISOString(),
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    form.reset();
    setSelectedFrequency('');
    onClose();
  };

  const frequency = form.watch('frequency');
  const transactionType = form.watch('type');

  // Update selected frequency when form changes
  useEffect(() => {
    setSelectedFrequency(frequency);
  }, [frequency]);

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {editingId
              ? 'Editar Transação Recorrente'
              : 'Nova Transação Recorrente'}
          </DialogTitle>
          <DialogDescription>
            {editingId
              ? 'Altere os detalhes da sua transação recorrente. As mudanças afetarão futuras ocorrências.'
              : 'Configure uma nova transação que será criada automaticamente de acordo com a frequência escolhida.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            {/* Type Selection */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo da Transação</FormLabel>
                  <FormControl>
                    <div className="flex gap-3">
                      <button
                        className={cn(
                          'flex flex-1 items-center gap-3 rounded-lg border p-4 transition-colors',
                          field.value === 'credit'
                            ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-950'
                            : 'border-border hover:bg-muted'
                        )}
                        onClick={() => field.onChange('credit')}
                        type="button"
                      >
                        <ArrowUpCircle className="h-5 w-5" />
                        <div className="text-left">
                          <div className="font-medium">Receita</div>
                          <div className="text-sm opacity-70">
                            Salário, freelance, etc.
                          </div>
                        </div>
                      </button>
                      <button
                        className={cn(
                          'flex flex-1 items-center gap-3 rounded-lg border p-4 transition-colors',
                          field.value === 'debit'
                            ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-950'
                            : 'border-border hover:bg-muted'
                        )}
                        onClick={() => field.onChange('debit')}
                        type="button"
                      >
                        <ArrowDownCircle className="h-5 w-5" />
                        <div className="text-left">
                          <div className="font-medium">Gasto</div>
                          <div className="text-sm opacity-70">
                            Aluguel, assinaturas, etc.
                          </div>
                        </div>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount and Description Row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute top-3 left-3 text-muted-foreground">
                          R$
                        </span>
                        <Input className="pl-9" placeholder="0,00" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecionar categoria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.data?.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.icon} {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        transactionType === 'credit'
                          ? 'Ex: Salário mensal, Freelance cliente X...'
                          : 'Ex: Aluguel, Netflix, Academia...'
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Frequency */}
            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequência</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Escolha a frequência" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="daily">Diário</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="biweekly">Quinzenal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                      <SelectItem value="quarterly">Trimestral</SelectItem>
                      <SelectItem value="yearly">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Conditional fields based on frequency */}
            {selectedFrequency === 'monthly' && (
              <FormField
                control={form.control}
                name="dayOfMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dia do Mês</FormLabel>
                    <FormControl>
                      <Input
                        max={31}
                        min={1}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              ? Number.parseInt(e.target.value)
                              : undefined
                          )
                        }
                        placeholder="Ex: 5 (todo dia 5)"
                        type="number"
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Deixe em branco para usar o mesmo dia da data de início
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {selectedFrequency === 'weekly' && (
              <FormField
                control={form.control}
                name="dayOfWeek"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dia da Semana</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(Number.parseInt(value))
                      }
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Escolha o dia da semana" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="0">Domingo</SelectItem>
                        <SelectItem value="1">Segunda-feira</SelectItem>
                        <SelectItem value="2">Terça-feira</SelectItem>
                        <SelectItem value="3">Quarta-feira</SelectItem>
                        <SelectItem value="4">Quinta-feira</SelectItem>
                        <SelectItem value="5">Sexta-feira</SelectItem>
                        <SelectItem value="6">Sábado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Start Date */}
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data de Início</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          className={cn(
                            'pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                          variant="outline"
                        >
                          {field.value ? (
                            format(field.value, 'PPP', { locale: ptBR })
                          ) : (
                            <span>Escolha a data de início</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                      <Calendar
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        initialFocus
                        mode="single"
                        onSelect={field.onChange}
                        selected={field.value}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Quando esta transação começará a ser criada automaticamente
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* End Date (Optional) */}
            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data de Fim (Opcional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          className={cn(
                            'pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                          variant="outline"
                        >
                          {field.value ? (
                            format(field.value, 'PPP', { locale: ptBR })
                          ) : (
                            <span>Sem data de fim (permanente)</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                      <Calendar
                        disabled={(date) =>
                          date <= (form.getValues('startDate') || new Date())
                        }
                        initialFocus
                        mode="single"
                        onSelect={field.onChange}
                        selected={field.value}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Deixe em branco se a transação não tem prazo de término
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Active Switch */}
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Transação Ativa</FormLabel>
                    <FormDescription>
                      Quando ativada, esta transação será criada automaticamente
                      nas datas especificadas
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Fixed Expense Switch */}
            <FormField
              control={form.control}
              name="isFixedExpense"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Gasto Fixo</FormLabel>
                    <FormDescription>
                      Marque esta transação como gasto fixo para cálculo do orçamento diário. Gastos fixos (aluguel, contas, etc.) não são considerados no seu orçamento diário disponível.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Form Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                className="flex-1"
                onClick={handleClose}
                type="button"
                variant="outline"
              >
                Cancelar
              </Button>
              <Button className="flex-1" disabled={isLoading} type="submit">
                {isLoading
                  ? 'Salvando...'
                  : editingId
                    ? 'Salvar Alterações'
                    : 'Criar Recorrência'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
